import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, X, Link as LinkIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImageUploader = ({ currentImage, onImageChange, label }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      onImageChange(fullUrl);
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onImageChange(urlInput.trim());
      setShowUrlInput(false);
      setUrlInput('');
      toast.success('Image URL updated');
    }
  };

  return (
    <div className="space-y-3">
      {label && <Label>{label}</Label>}
      
      {currentImage && (
        <div className="relative w-full h-32 bg-sand rounded-xl overflow-hidden">
          <img 
            src={currentImage} 
            alt="Current" 
            className="w-full h-full object-contain"
          />
          <button
            onClick={() => onImageChange('')}
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex-1 rounded-xl text-sm"
          size="sm"
        >
          {uploading ? (
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
          ) : (
            <Upload className="w-4 h-4 mr-2" />
          )}
          Upload
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowUrlInput(!showUrlInput)}
          className="rounded-xl text-sm"
          size="sm"
        >
          <LinkIcon className="w-4 h-4 mr-1" />
          URL
        </Button>
      </div>

      {showUrlInput && (
        <div className="flex gap-2">
          <Input
            placeholder="Paste image URL..."
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            className="rounded-xl text-sm"
          />
          <Button onClick={handleUrlSubmit} size="sm" className="rounded-xl bg-forest hover:bg-forest-dark text-white">
            Add
          </Button>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
